import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { accountDetailPath } from '../../lib/portal';
import { formatPhoneDisplay, phoneTelHref } from '../../lib/intlPhone';
import type { Client, Contact } from '../../types/crm';
import { contactLabel } from '../../utils/crmHelpers';
import { groupContactAccountsForDisplay } from '../../utils/contactDedup';

interface ContactListRowProps {
  contact: Contact;
  account?: Client;
  accounts?: Client[];
  allClients: Client[];
  salespersonCode: string;
  /** IT Management — omit salesperson column. */
  hideSalesperson?: boolean;
  onOpen: () => void;
}

function initials(contact: Contact): string {
  const a = contact.first_name?.[0] ?? '';
  const b = contact.last_name?.[0] ?? '';
  return (a + b).toUpperCase() || '?';
}

function telHref(phone: string): string {
  return phoneTelHref(phone) || `tel:${phone}`;
}

export function ContactListRow({
  contact,
  account,
  accounts,
  allClients,
  salespersonCode,
  hideSalesperson = false,
  onOpen,
}: ContactListRowProps) {
  const name = contactLabel(contact);
  const linkedAccounts = accounts ?? (account ? [account] : []);
  const accountGroups = useMemo(
    () => groupContactAccountsForDisplay(linkedAccounts, allClients),
    [linkedAccounts, allClients],
  );
  const email = contact.email?.trim() ?? '';
  const phone = contact.phone?.trim() ?? '';

  return (
    <div
      className={[
        'contact-list-row',
        'contact-list-row--clickable',
        hideSalesperson ? 'contact-list-row--no-salesperson' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="contact-list-row__avatar" aria-hidden>
        {initials(contact)}
      </div>

      <div className="contact-list-row__main">
        <span className="contact-list-row__name">{name}</span>
        <span className="contact-list-row__sub">{contact.job_title?.trim() || '—'}</span>
      </div>

      <div className="contact-list-row__account">
        {accountGroups.length === 0 ? (
          <span className="contact-list-row__sub">No account</span>
        ) : (
          <div className="contact-list-row__account-groups">
            {accountGroups.map((group) => (
              <div key={group.parent.id} className="contact-list-row__account-group">
                <Link
                  to={accountDetailPath(group.parent.code)}
                  className="contact-list-row__account-parent"
                  title={group.parent.code}
                  onClick={(e) => e.stopPropagation()}
                >
                  {group.parent.company_name}
                </Link>
                {group.brands.length > 0 ? (
                  <div className="contact-list-row__account-brands">
                    {group.brands.map((brand, idx) => (
                      <span key={brand.id} className="contact-list-row__account-brands-item">
                        <Link
                          to={accountDetailPath(brand.code)}
                          className="contact-list-row__account-brand"
                          title={brand.code}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {brand.company_name}
                        </Link>
                        {idx < group.brands.length - 1 ? (
                          <span className="contact-list-row__accounts-dot" aria-hidden>
                            ·
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </div>
                ) : group.parent.code ? (
                  <div className="contact-list-row__account-brands">
                    <span className="contact-list-row__account-brand contact-list-row__account-brand--static">
                      {group.parent.code}
                    </span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="contact-list-row__reach">
        {email ? (
          <a
            href={`mailto:${email}`}
            className="contact-list-row__reach-email contact-list-row__link"
            onClick={(e) => e.stopPropagation()}
          >
            {email}
          </a>
        ) : (
          <span className="contact-list-row__reach-email">—</span>
        )}
        {phone ? (
          <a
            href={telHref(phone)}
            className="contact-list-row__reach-phone contact-list-row__link"
            onClick={(e) => e.stopPropagation()}
          >
            {formatPhoneDisplay(phone)}
          </a>
        ) : (
          <span className="contact-list-row__reach-phone">—</span>
        )}
      </div>

      {!hideSalesperson ? (
        <span className="contact-list-row__owner mono">{salespersonCode}</span>
      ) : null}
      <span className="contact-list-row__chevron" aria-hidden>
        ›
      </span>
    </div>
  );
}
